import { useEffect, useState } from "react";
import {
  api,
  getProductCatalog,
  getDesignRequestsByCampaignId,
  getPublishingProductsByCampaignId,
  getDesignedProductTemplatesByCampaignId,
  getDesignFilesByCampaignId,
  getLicenseByCampaign,
} from "../../services/axios";
import { DesignRequest } from "../../utils/types";
import { DesignHistory } from "./components/design-requests/design-history-table";
import { FileHistoryTable } from "./components/files/file-history-table";
import { FileUploader } from "./components/files/file-uploader";
import { LicensingTable } from "./components/licensing/licensing-table";
import { ProductsTable } from "./components/products/products-table";
import { CreateCampaign } from "./components/campaign/create-campaign";
import { CreateDesignRequestsForm } from "./components/requests/create-request";
import { DesignProductTemplatesTable } from "./components/design-product-templates/template-table";
import { auth } from "../../config/firebase";

type NextType = {
  createdAt: string;
  id: string;
};

export function Campaign(props: any) {
  const user = auth.currentUser?.uid;

  let CID = props.match.params.id;

  let [catalogProducts, setCatalogProducts] = useState([]);
  let [designFiles, setDesignFiles] = useState([]);

  let [designPages, setDesignPages] = useState<NextType[]>([]);
  let [designRequests, setDesignRequests] = useState<DesignRequest[]>([]);

  let [licensingPages, setLicensingPages] = useState<NextType[]>([]);
  let [licensing, setLicensing] = useState([]);

  let [productPages, setProductPages] = useState<NextType[]>([]);
  let [publishedProducts, setPublishedProducts] = useState([]);

  let [designProductTemplatePages, setDesignProductTemplatePages] = useState<
    NextType[]
  >([]);
  let [designProductTemplates, setDesignProductTemplates] = useState([]);

  useEffect(() => {
    const fetchCampaign = async (CID: string) => {
      await getFiles(CID);
      await getCatalogProducts();
      await getDesignRequests(CID);
      await getPublishingProducts(CID);
      await getLicensingDocuments(CID);
      await getDesignedProductTemplates(CID);
    };
    fetchCampaign(CID);
  }, [CID]);

  async function getCatalogProducts() {
    const prodQuery = await getProductCatalog();
    let data = prodQuery.map(function (data: any) {
      return {
        label: `${data.brand} ${data.model} ${data.sku}`,
        value: data,
      };
    });
    setCatalogProducts(data);
  }

  async function getFiles(CID: string) {
    const files = await getDesignFilesByCampaignId(CID);
    setDesignFiles(files);
  }

  async function getDesignRequests(
    CID: string,
    next?: { createdAt: string; id: string }
  ) {
    console.log("designPages", designPages);
    const designs = await getDesignRequestsByCampaignId(CID, next);
    if (designs.items && !designs.items.length) {
      setDesignRequests([]);
      return;
    }
    setDesignRequests(designs.items);
    if (designs.next) {
      setDesignPages([
        ...designPages,
        { id: designs.next, createdAt: designs.createdAt },
      ]);
    }
  }

  async function getPreviousDesignRequestPage() {
    if (designPages.length === 0) {
      return;
    }
    if (designPages.length === 1) {
      const designs = await getDesignRequestsByCampaignId(CID);
      setDesignRequests(designs.items);
    }
    if (designPages.length > 1) {
      setDesignPages(designPages.slice(0, -1));
      const next = designPages[designPages.length - 1];
      const designs = await getDesignRequestsByCampaignId(CID, next);
      setDesignRequests(designs.items);
    }
  }

  async function getDesignedProductTemplates(
    CID: string,
    next?: { createdAt: string; id: string }
  ) {
    const templates = await getDesignedProductTemplatesByCampaignId(CID, next);
    if (templates.items && !templates.items.length) {
      setDesignProductTemplates([]);
      return;
    }
    setDesignProductTemplates(templates.items);
    if (templates.next) {
      setDesignProductTemplatePages([
        ...designProductTemplatePages,
        { id: templates.next, createdAt: templates.createdAt },
      ]);
    }
  }

  async function getPreviousDesignedProductTemplatePage() {
    if (designProductTemplatePages.length === 0) {
      return;
    }
    if (designProductTemplatePages.length === 1) {
      const designs = await getDesignedProductTemplatesByCampaignId(CID);
      setDesignProductTemplates(designs.items);
    }
    if (designProductTemplatePages.length > 1) {
      setDesignProductTemplatePages(designProductTemplatePages.slice(0, -1));
      const next =
        designProductTemplatePages[designProductTemplatePages.length - 1];
      const designs = await getDesignedProductTemplatesByCampaignId(CID, next);
      setDesignProductTemplates(designs.items);
    }
  }

  async function getPublishingProducts(
    CID: string,
    next?: { createdAt: string; id: string }
  ) {
    let products = await getPublishingProductsByCampaignId(CID, next);
    if (products.items && !products.items.length) {
      setPublishedProducts([]);
      return;
    }
    setPublishedProducts(products.items);
    if (products.next) {
      setProductPages([
        ...productPages,
        { id: products.next, createdAt: products.createdAt },
      ]);
    }
  }

  async function getPreviousProductPage() {
    if (productPages.length === 0) {
      return;
    }
    if (productPages.length === 1) {
      let products = await getPublishingProductsByCampaignId(CID);
      setPublishedProducts(products.items);
    }
    if (productPages.length > 1) {
      setProductPages(productPages.slice(0, -1));
      let next = productPages[productPages.length - 1];
      let products = await getPublishingProductsByCampaignId(CID, next);
      setPublishedProducts(products.items);
    }
  }

  async function getLicensingDocuments(
    CID: string,
    next?: { createdAt: string; id: string }
  ) {
    const licensing = await getLicenseByCampaign(CID, next);
    if (licensing.items && !licensing.items.length) {
      setLicensing([]);
      return;
    }
    setLicensing(licensing.items);
    if (licensing.next) {
      setLicensingPages([
        ...licensingPages,
        { id: licensing.next, createdAt: licensing.createdAt },
      ]);
    }
    await queryLicenseStatus(licensing.items);
  }

  async function getPreviousLicensingPage() {
    if (licensingPages.length === 0) {
      return;
    }
    if (licensingPages.length === 1) {
      const licensing = await getLicenseByCampaign(CID);
      setLicensing(licensing.items);
    }
    if (licensingPages.length > 1) {
      setLicensingPages(licensingPages.slice(0, -1));
      const next = licensingPages[licensingPages.length - 1];
      const li = await getLicenseByCampaign(CID, next);
      setLicensing(li.items);
    }
  }

  async function queryLicenseStatus(licensing: any[]) {
    const pending = licensing.filter((x) => x.status === "Affinty Requested");
    for (let i = 0; i < pending.length; i++) {
      let affinity_license_id = pending[i].affinity_license_id;
      const licensingResult = await api.get(
        `/licensing/affinity/${affinity_license_id}`
      );
      const licenseData = licensingResult.data.Items;
      if (licenseData.length === 0 || licenseData.status === "not ready") {
        continue;
      }
      const first = licenseData.pop();
      if (!first) {
        continue;
      }
      const status = first.status;
      if (!status) {
        continue;
      }
      const affinity_status_id = first.affinity_status_id;
      if (!affinity_status_id) {
        continue;
      }
      await api
        .put(`licensing/${pending[0].id}`, {
          status: status,
          affinity_status_id: affinity_status_id,
        })
        .catch((e) => console.log(e));
    }
  }

  return (
    <div className="bg-white w-full flex justify-center">
      <div className="flex-col justify-between items-center p-16">
        <CreateCampaign cid={CID} />
        <FileUploader cid={CID} user={user} callback={() => getFiles(CID)} />
        <FileHistoryTable designFiles={designFiles} />
        <CreateDesignRequestsForm
          cid={CID}
          designFiles={designFiles}
          catalogProducts={catalogProducts}
        />
        <DesignHistory
          designs={designRequests}
          page={designPages.length}
          previousPage={() => getPreviousDesignRequestPage()}
          nextPage={() =>
            getDesignRequests(CID, designPages[designPages.length - 1])
          }
        />
        <DesignProductTemplatesTable
          templates={designProductTemplates}
          page={designProductTemplatePages.length}
          previousPage={() => getPreviousDesignedProductTemplatePage()}
          nextPage={() =>
            getDesignedProductTemplates(
              CID,
              designProductTemplatePages[designProductTemplatePages.length - 1]
            )
          }
        />
        <ProductsTable
          licensing={licensing}
          publishedProducts={publishedProducts}
          cid={CID}
          page={productPages.length}
          previousPage={() => getPreviousProductPage()}
          nextPage={() =>
            getPublishingProducts(CID, productPages[productPages.length - 1])
          }
        />
        <LicensingTable
          licensing={licensing}
          page={licensingPages.length}
          previousPage={() => getPreviousLicensingPage()}
          nextPage={() =>
            getLicensingDocuments(
              CID,
              licensingPages[licensingPages.length - 1]
            )
          }
        />
      </div>
    </div>
  );
}
